// The three phases of a game, decided once when a game opens so that every
// tab, the puzzles and the history agree on which ply belongs where. The
// opening ends with the move that left the book (or at move 12 when the
// book never knew the game); the endgame starts by the rule of
// `@chess/api-ending-case` (`endgameStartOf`: Speelman's 13 points a side or
// Lichess's 6 pieces in all), re-exported here.
import type { Color, Fen, Phase, ReviewPhases } from '@chess/api-model';
import { Score, sideToMove, type EngineSideScore, type ReaderScore } from './score.ts';
import { Stages } from './stages.ts';

export { ENDGAME_MATERIAL, ENDGAME_PIECES, isEndgame, endgameStartOf } from '@chess/api-ending-case';
export { sideToMove, MATE_PAWNS, type EngineSideScore, type ReaderScore } from './score.ts';

/** A game the book never knew is taken to leave the opening at move 12. */
export const FALLBACK_OPENING_PLIES = 24;

/**
 * Where the opening ends: the move that left the book, that move included,
 * since leaving theory is the opening's last decision; the whole game when
 * the book was never left; move 12 when the book never knew the game.
 */
export function openingEndOf(moveCount: number, lastKnownPly: number | undefined): number {
  if (lastKnownPly === undefined) return Math.min(FALLBACK_OPENING_PLIES, moveCount);
  if (lastKnownPly >= moveCount) return moveCount;
  return Math.min(lastKnownPly + 1, moveCount);
}

/**
 * The positions a phase spans: `fromPly` is the position it starts from,
 * `toPly` its last; its moves are `fromPly + 1 … toPly`. Spans are exclusive
 * and contiguous, and each ends on the reader's move where it is scored, so
 * pass the reader (White when unknown). Delegates to `Stages`.
 */
export function phaseRange(phases: ReviewPhases, moveCount: number, phase: Phase, reader: Color = 'white'): { fromPly: number; toPly: number } {
  return new Stages(phases, moveCount, reader).range(phase);
}

// ---------------------------------------------------------------------------
// Scores from the reader's side. The scan stores every score for the side to
// move; anything shown to the reader is turned to their side first, and the
// stage header and the opening history read the opening's end through the
// one rule below so their numbers can never differ.


/** What `openingExit` needs of a scan: positions with a score for the side to move. */
export interface ScoredPosition {
  ply: number;
  fen: Fen;
  score: EngineSideScore;
}

/** The side that played the move at a 1-based ply. */
export function moverOf(ply: number): Color {
  return (ply - 1) % 2 === 0 ? 'white' : 'black';
}

/** A score for the side to move, seen from `reader`'s side. Delegates to `Score`. */
export function scoreForReader(score: EngineSideScore, mover: Color, reader: Color): ReaderScore {
  return Score.fromEngine(score, mover, reader).toJSON();
}

/** A reader-side score in pawns; mates saturate. Delegates to `Score`. */
export function readerPawns(score: ReaderScore): number {
  return Score.of(score).pawns();
}

/** The app's notation from the reader's side. Delegates to `Score`. */
export function readerScoreText(score: ReaderScore): string {
  return Score.of(score).text();
}

/** The ply of the reader's first move after `toPly`, or `toPly` itself when none follows. */
export function readerMoveAfter(toPly: number, moveCount: number, reader: Color): number {
  for (let ply = toPly + 1; ply <= moveCount; ply++) {
    if (moverOf(ply) === reader) return ply;
  }
  return toPly;
}

/**
 * Where the opening's score is read and what it is: the position after the
 * reader's first move following the opening (the move that left the book
 * when the reader played it, or the reader's reply when the opponent did),
 * from the reader's side. The game starts level, so this is also the
 * opening's whole change. `score` is undefined until the scan has the ply.
 */
export function openingExit(
  scan: { positions: ReadonlyArray<ScoredPosition> } | undefined,
  phases: ReviewPhases,
  moveCount: number,
  reader: Color,
): { fromPly: 0; toPly: number; score?: ReaderScore } {
  // The opening's span already ends on the reader's move where it is scored.
  const { toPly } = phaseRange(phases, moveCount, 'opening', reader);
  const position = scan?.positions.find((p) => p.ply === toPly);
  const score = position ? scoreForReader(position.score, sideToMove(position.fen), reader) : undefined;
  return { fromPly: 0, toPly, score };
}

/** The phase the move at a 1-based ply belongs to, with the spans scored for `reader`. */
export function phaseOfMove(phases: ReviewPhases, moveCount: number, ply: number, reader: Color = 'white'): Phase {
  return new Stages(phases, moveCount, reader).ofMove(ply);
}
